import { getFirestore, doc, getDoc, setDoc } from 'firebase/firestore';

class ContactCaptureService {
  constructor() {
    this.db = getFirestore();
  }

  /**
   * Add shared contacts to a page owner's contact list
   * @param {string} pageOwnerId - UID of the page owner whose page is being shared
   * @param {Array} contacts - Array of contact objects with email/phone
   * @returns {Promise<Object>} Result object with success status and details
   */
  async addSharedContacts(pageOwnerId, contacts) {
    try {
      
      

      // Reference to the page owner's shared contacts document
      const sharedContactsRef = doc(this.db, "sharedContacts", pageOwnerId);
      
      // Get existing contacts
      const existingDoc = await getDoc(sharedContactsRef);
      let existingContacts = [];
      
      if (existingDoc.exists()) {
        existingContacts = existingDoc.data().contacts || [];
        
      } else {
        
      }
      
      // Add new contacts (avoid duplicates based on email or phone)
      const existingEmails = new Set(existingContacts.map(c => c.email?.toLowerCase()).filter(Boolean));
      const existingPhones = new Set(existingContacts.map(c => c.phone).filter(Boolean));
      
      const newContacts = contacts.filter(contact => {
        // Skip if email already exists
        if (contact.email && existingEmails.has(contact.email.toLowerCase())) {
          return false;
        }
        // Skip if phone already exists
        if (contact.phone && existingPhones.has(contact.phone)) {
          return false;
        }
        return true;
      });
      
      // Combine existing and new contacts
      const allContacts = [...existingContacts, ...newContacts.map(contact => ({
        ...contact,
        addedAt: new Date().toISOString()
      }))];
      
      // Save to Firestore
      const documentData = {
        pageOwnerId: pageOwnerId,
        contacts: allContacts,
        lastUpdated: new Date().toISOString(),
        totalContacts: allContacts.length
      };

      await setDoc(sharedContactsRef, documentData);
      
      
      
      
      
      // Verify save worked
      const verifyDoc = await getDoc(sharedContactsRef);
      if (verifyDoc.exists()) {
        
        return { 
          success: true, 
          totalContacts: allContacts.length, 
          newContactsAdded: newContacts.length 
        };
      } else {
        
        return { success: false, error: 'Document verification failed' };
      }
      
    } catch (error) {
      
      
      return { success: false, error: error.message };
    }
  }

  /**
   * Get shared contacts for a page owner
   * @param {string} pageOwnerId - UID of the page owner
   * @returns {Promise<Array>} Array of contact objects
   */
  async getSharedContacts(pageOwnerId) {
    try {
      const sharedContactsRef = doc(this.db, "sharedContacts", pageOwnerId);
      const contactsDoc = await getDoc(sharedContactsRef);
      
      if (contactsDoc.exists()) {
        const data = contactsDoc.data();
        
        return data.contacts || [];
      } else {
        
        return [];
      }
    } catch (error) {
      
      return [];
    }
  }

  /**
   * Create contact objects from email addresses
   * @param {Array<string>} emails - Array of email addresses
   * @param {string} source - Source of the contacts (e.g., 'email_share')
   * @returns {Array} Array of contact objects
   */
  createEmailContacts(emails, source = 'email_share') {
    return emails.map(email => ({
      email: email.toLowerCase().trim(),
      firstName: '',
      lastName: '',
      phone: null,
      source: source,
      addedAt: new Date().toISOString()
    }));
  }

  /**
   * Create contact objects from phone numbers
   * @param {Array<string>} phones - Array of phone numbers
   * @param {string} source - Source of the contacts (e.g., 'sms_share')
   * @returns {Array} Array of contact objects
   */
  createPhoneContacts(phones, source = 'sms_share') {
    return phones.map(phone => ({
      email: '',
      firstName: '',
      lastName: '',
      phone: phone.trim(),
      source: source,
      addedAt: new Date().toISOString()
    }));
  }
}

export default ContactCaptureService;